import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { CircularLoading } from '../../Common';
import ToolBar from '../DataGridToolbar/index.js';
import { Box, Card, IconButton, TablePagination, tablePaginationClasses, Tooltip } from '@mui/material';

import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';

import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import UserInfo from '../../../UserInfo';
import { trakrefFontFamilySemiBold } from '../../../utils';
import {
    ColumnMenu,
    generateColumn,
    generateFilterModel,
    generateSortModel,
    getDefaultPage,
    getDefaultSortBy,
    getSortFromSavedState,
    getTogglableColumns,
    saveTableState,
    NoData,
    getLocalStorageKey,
    validateAndCleanCachedState
} from '../DataGrid/utils.js';
import NoDataTableIcon from '../../../Icons/empty_table_placeholder.svg';
import '../Table/Table.css';
import { trakrefBlue } from '../../../color_utils.js';
import { isMobile } from 'react-device-detect';
import { useScrollToElement } from '../../../hooks/useScrollToElement.js';
import usePrintLandscape from '../../../hooks/usePrintLandscape';

const DataGridWithExpansionPanel = ({
    tableId,
    url,
    defaultSort,
    rowCount,
    page,
    refresh,
    exportButtonURL,
    exportButtonAlt,
    exportFileName,
    printButtonURL,
    printButtonAlt,
    noResultText,
    noResultText2,
    noResultText3,
    tableHeader,
    tableHeaderTooltipText,
    filterOptions,
    hideExportButton,
    columns,
    columnDependencies,
    getDetailPanelContent,
    getDetailPanelHeight,
    exportFileTypes,
    setTableFilters,
    onDetailPanelExpandedRowIdsChange,
    detailPanelExpandedRowIds,
    headerContent,
    dataGridSx,
    printLandscape
}) => {
    const gridApiRef = useGridApiRef();
    const print = usePrintLandscape(printLandscape);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(rowCount);
    const [initialColumns, setInitialColumns] = useState({});
    const [initialState, setInitialState] = useState({});
    const [resultCount, setResultCount] = useState(0);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [topRef, scrollToTop] = useScrollToElement();

    const LOCAL_STORAGE_TABLE_STATE = getLocalStorageKey(url);

    const memoizedColumns = useMemo(() => {
        const cachedState = validateAndCleanCachedState(LOCAL_STORAGE_TABLE_STATE, columns);
        const orderedFields = cachedState?.columns?.orderedFields;

        if (Array.isArray(orderedFields) && orderedFields.length > 0) {
            // Filter out any undefined columns (extra safety)
            return orderedFields
                .map(field => columns.find(col => col.field === field))
                .filter(col => col !== undefined)
                .map(col => generateColumn(col, filterOptions));
        }

        return columns.map(col => generateColumn(col, filterOptions));
    }, [filterOptions, columnDependencies, columns]);

    const [tableRequest, setTableRequest] = useState({
        page: getDefaultPage(url, page),

        sortBy: getSortFromSavedState(LOCAL_STORAGE_TABLE_STATE, getDefaultSortBy(url, defaultSort)),
        filters: (() => {
            const cachedState = validateAndCleanCachedState(LOCAL_STORAGE_TABLE_STATE, columns);
            return generateFilterModel(cachedState?.filter?.filterModel?.items);
        })()
    });

    useEffect(() => {
        const cachedState = validateAndCleanCachedState(LOCAL_STORAGE_TABLE_STATE, columns);
        let columnInitialVisibility = {};

        if (cachedState?.filter?.filterModel) {
            setInitialState(cachedState);
            setFilterModel(cachedState.filter.filterModel);
        }

        columns.forEach(({ field, initiallyVisible = true }) => (
            columnInitialVisibility[field] = initiallyVisible
        ));

        setInitialColumns(columnInitialVisibility);
    }, []);

    useEffect(() => {
        fetchData();
        if (setTableFilters) {
            setTableFilters(tableRequest);
        }
    }, [tableRequest, refresh, url]);

    const fetchData = async () => {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    rowCount: rowsPerPage,
                    page: tableRequest.page,
                    filters: tableRequest.filters,
                    sortBy: tableRequest.sortBy
                })
            });

            const data = await response.json();
            const { rows, resultCount } = data;

            setRows(rows);
            setResultCount(resultCount);
        }
        catch {
            setLoading(false);
        }
        finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    const changePage = (amount) => {
        setTableLoading(true);
        sessionStorage.setItem(url + "page" + UserInfo.instanceId, JSON.stringify(tableRequest.page + amount));
        setTableRequest(prev => ({
            ...prev,
            page: prev.page + amount
        }));
    };

    const handleChangeRowsPerPage = (event) => {
        setTableLoading(true);
        setRowsPerPage(parseInt(event.target.value, 10));
        setTableRequest(prev => ({ ...prev, page: 1 }));
    };

    const handleFirstPageButtonClick = () => {
        scrollToTop();
        changePage(-tableRequest.page + 1);
    }

    const handleLastPageButtonClick = () => {
        scrollToTop();
        changePage(Math.max(0, Math.ceil(resultCount / rowsPerPage) - tableRequest.page))
    }

    const changePageBack = () => {
        scrollToTop();
        changePage(-1);
    }

    const changePageForward = () => {
        scrollToTop();
        changePage(1);
    }


    const PaginationActions = () => (
        <Box sx={{ flexShrink: 0 }}>
            <Tooltip title='First Page'>
                <IconButton
                    onClick={handleFirstPageButtonClick}
                    disabled={tableRequest.page === 1}
                    aria-label="first page"
                    sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
                >
                    <FirstPageIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title='Previous Page'>
                <IconButton
                    onClick={changePageBack}
                    disabled={tableRequest.page === 1}
                    aria-label="previous page"
                >
                    <KeyboardArrowLeft />
                </IconButton>
            </Tooltip>
            <Tooltip title='Next Page'>
                <IconButton
                    onClick={changePageForward}
                    disabled={tableRequest.page > Math.ceil(resultCount / rowsPerPage) - 1}
                    aria-label="next page"
                >
                    <KeyboardArrowRight />
                </IconButton>
            </Tooltip>
            <Tooltip title='Last Page'>
                <IconButton
                    onClick={handleLastPageButtonClick}
                    disabled={tableRequest.page > Math.ceil(resultCount / rowsPerPage) - 1}
                    aria-label="last page"
                    sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
                >
                    <LastPageIcon />
                </IconButton>
            </Tooltip>
            {/* Input field for page number - might need this in the future */}
            {/* <Input
                label='Page'
                variant='standard'
                value={tableRequest.page}
                onChange={e => {
                    // changePage(-tableRequest.page + 1 + Number(e.target.value))
                    console.log('value', e.target.value)
                }}
            /> */}
        </Box>
    );

    const Pagination = () => (
        <TablePagination
            sx={{
                [`& .${tablePaginationClasses.selectLabel}`]: { fontFamily: trakrefFontFamilySemiBold, display: { xs: 'none', lg: 'block' } },
                [`& .${tablePaginationClasses.displayedRows}`]: { fontFamily: trakrefFontFamilySemiBold },
                [`& .${tablePaginationClasses.spacer}`]: { display: "none" },
                [`& .${tablePaginationClasses.toolbar}`]: { justifyContent: "center", paddingLeft: 2 },
                [`& .MuiTablePagination-input`]: { display: { xs: 'none', lg: 'inline-flex' } },
                [`& .${tablePaginationClasses.selectIcon}`]: { display: { xs: 'none', lg: 'block' } }
            }}
            rowsPerPageOptions={[5, 10, 50, 100]}
            count={resultCount}
            rowsPerPage={rowsPerPage}
            // MUI TablePagination is built using a starting index of 0..
            // ..but we start at 1, so must subtract 1 in order to see proper results 
            page={tableRequest.page - 1}
            slotProps={{
                select: {
                    inputProps: {
                        'aria-label': 'rows per page',
                    },
                },
            }}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={PaginationActions}
        />
    );

    const clearAllFilters = (newFilterModel) => {
        setFilterModel(newFilterModel);
        setTableRequest(prev => ({
            ...prev,
            page: 1,
            filters: []
        }));
        saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE);
    };

    // Construct filters array & append to table query
    const handleFilterModelChange = useCallback((newFilterModel) => {
        const { items } = newFilterModel;
        // console.log('what is items', items)
        setFilterModel(newFilterModel);

        // Clearing all filters
        if (items.length <= 0) {
            return clearAllFilters(newFilterModel);
        }

        const filters = generateFilterModel(items);
        console.log('filters', filters)

        setTableLoading(true);
        setTableRequest(prev => ({ ...prev, page: 1, filters }));
        saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE);
    }, []);

    const handleSortModelChange = useCallback((newSortModel) => {
        const sortBy = generateSortModel(newSortModel);

        setTableLoading(true);
        setTableRequest(prev => ({ ...prev, page: 1, sortBy }));
        saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE);
    }, []);

    if (loading) {
        return (
            <CircularLoading size={50} marginTop={3} />
        );
    }

    return (
        <Card ref={print.ref} variant='outlined' className={print.className} style={{ height: rows.length > 0 ? "auto" : "500px" }}>
            <div ref={topRef} />
            {headerContent && headerContent}
            <DataGridPro
                pagination
                disableRowSelectionOnClick
                apiRef={gridApiRef}
                rows={rows}
                columns={memoizedColumns}
                loading={tableLoading}
                columnHeaderHeight={45}
                filterMode='server'
                sortingMode='server'
                onFilterModelChange={handleFilterModelChange}
                onSortModelChange={handleSortModelChange}
                localeText={{
                    toolbarExportCSV: exportButtonAlt,
                    toolbarColumnsLabel: 'Customize Columns',
                    "headerFilterOperatorFrom-to": "From - To",
                    "headerFilterOperatorBetween": "From - To",
                }}
                slots={{
                    pagination: Pagination,
                    toolbar: () => (
                        <Box sx={{ py: { xs: '22px', lg: 0 } }}>
                            <ToolBar
                                exportButtonTooltip={exportButtonAlt}
                                exportFileTypes={exportFileTypes}
                                hideExportButton={hideExportButton}
                                exportOptions={{
                                    ...tableRequest,
                                    exportFileName,
                                    exportURL: exportButtonURL,
                                }}
                            />
                        </Box>
                    ),
                    noRowsOverlay: () => (
                        <NoData
                            noTableDataIcon={NoDataTableIcon}
                            noResultText={noResultText}
                            noResultText2={noResultText2}
                            noResultText3={noResultText3}
                        />
                    ),
                    columnMenu: ColumnMenu,
                    headerFilterMenu: null
                }}
                slotProps={{
                    columnsManagement: {
                        getTogglableColumns,
                        disableResetButton: true
                    },
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'linear-progress',
                    },
                    filterPanel: {
                        logicOperators: isMobile ? [] : ['and'],
                        sx: isMobile ? {
                            width: '100%',
                            "& .MuiDataGrid-filterFormOperatorInput": {
                                width: '100px'
                            }
                        } : {},
                    }
                }}
                initialState={{
                    // initially show predetermined columns
                    columns: {
                        columnVisibilityModel: initialColumns,
                    },
                    // spread initialState afterward to load saved table modifications
                    ...initialState,
                }}
                getRowClassName={params => params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'}
                getDetailPanelContent={getDetailPanelContent}
                onDetailPanelExpandedRowIdsChange={onDetailPanelExpandedRowIdsChange}
                getDetailPanelHeight={getDetailPanelHeight}
                onColumnVisibilityModelChange={() => saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE)}
                onColumnOrderChange={() => saveTableState(gridApiRef, LOCAL_STORAGE_TABLE_STATE)}
                filterModel={filterModel}
                filterDebounceMs={350}
                detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                sx={{
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: trakrefBlue,
                    },
                    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                        outline: `2px solid ${trakrefBlue}`
                    },
                    '& .MuiDataGrid-menuIcon': {
                        visibility: 'visible',
                        width: 'auto',
                        opacity: 1,
                    },
                    '& .MuiDataGrid-footerContainer': {
                        justifyContent: { xs: 'flex-start', lg: 'center' },
                    },
                    border: 'none',
                    ...dataGridSx
                }}
            />
        </Card>
    );
};

DataGridWithExpansionPanel.propTypes = {
    tableId: PropTypes.string,
    url: PropTypes.string.isRequired,
    page: PropTypes.number,
    rowCount: PropTypes.number,
    defaultSort: PropTypes.string,
    refresh: PropTypes.bool,
    exportButtonURL: PropTypes.string,
    exportButtonAlt: PropTypes.string,
    exportFileName: PropTypes.string,
    printButtonURL: PropTypes.string,
    printButtonAlt: PropTypes.string,
    noResultText: PropTypes.string,
    noResultText2: PropTypes.string,
    noResultText3: PropTypes.string,
    tableHeader: PropTypes.string,
    tableHeaderTooltipText: PropTypes.string,
    filterOptions: PropTypes.array,
    subTableColumns: PropTypes.array,
    hideExportButton: PropTypes.bool,
    getDetailPanelContent: PropTypes.func,
    getDetailPanelHeight: PropTypes.func,
    exportFileTypes: PropTypes.array,
    onDetailPanelExpandedRowIdsChange: PropTypes.func,
    detailPanelExpandedRowIds: PropTypes.array,
    columnDependencies: PropTypes.any,
    headerContent: PropTypes.element,
    dataGridSx: PropTypes.object,
    printLandscape: PropTypes.bool
};

DataGridWithExpansionPanel.defaultProps = {
    page: 1,
    rowCount: 10,
    defaultSort: "",
    refresh: false,
    exportButtonURL: "",
    exportButtonAlt: "",
    exportFileName: "",
    printButtonURL: "",
    printButtonAlt: "",
    noResultText: "",
    noResultText2: "",
    noResultText3: "",
    tableHeader: "",
    tableHeaderTooltipText: "",
    filterOptions: [],
    subTableColumns: [],
    hideExportButton: false,
    getDetailPanelContent: () => { },
    exportFileTypes: [{ type: 'csv', name: 'CSV' }],
    getDetailPanelHeight: () => "auto",
    columnDependencies: null,
    headerContent: null,
    printLandscape: false
};

export default DataGridWithExpansionPanel;
